/**
 * Permits fidelity Phase A — the dynamic, schema-driven form renderer (docs/design/
 * permits-fidelity-ui.md §1/§2/§3, docs/features/permits-fidelity.md §A.3). Walks a `FormSchema`'s
 * `sections` in order, rendering each as a collapsible `td-formsection` disclosure, dispatching each
 * `FormField` on its `type` (text/textarea/number/select/checkbox/checkbox-group/date/dimensions/
 * section-note), honoring one-level `showWhen` conditional visibility, and collecting answers into a
 * single flat `FormValues` map keyed by `field.key` — the SAME renderer drives all six permit types
 * (the Building form is just the biggest schema, not a special case).
 *
 * a11y (WCAG 2.1 AA, ledger §10 / ui-spec §10): every section header is a real
 * `<button aria-expanded>`; checkbox-group fields are `role="group"` with a legend; a `showWhen`
 * reveal is wrapped in `aria-live="polite"` so it's announced, not silent; a collapsed section's
 * fields are NOT rendered at all (not `display:none`) so keyboard focus order never includes hidden
 * controls; every control reuses the existing `Field` component for label/helper/error wiring.
 *
 * XSS note: every rendered value is plain React text content (auto-escaped) — no
 * `dangerouslySetInnerHTML` anywhere in this file, applicant-authored or otherwise.
 */
import { useId, useState } from "react";
import type { FormField, FormSchema, FormValues } from "../api/types.js";
import { Field } from "./Field.js";
import { AlertIcon } from "./icons.js";

export type { FormValues };

interface SchemaFormRendererProps {
  schema: FormSchema;
  values: FormValues;
  onChange: (next: FormValues) => void;
  /** Keys with a validation error (e.g. "formValues.owner_name" -> the trailing key), rendered as a
   *  per-field error, same convention as the rest of the intake forms' `fieldErrors` maps. */
  fieldErrors?: Record<string, string>;
  /** Sections that start expanded (ui-spec §1 "Universal base ... always renders open by default;
   *  every other section starts collapsed"). Defaults to just the first section. */
  initialOpenKeys?: string[];
  /** OCR/AI-pre-fill "double-check" confidence flags (permit-poa-digest.md "Log a paper permit" —
   *  the ONLY consumer today is LogPaperPermitPage's review step). Keyed by field key -> helper text
   *  explaining why it's flagged. A flagged field gets a dashed warning-toned border + an inline
   *  "Double-check" badge (icon + text, never color alone); its section's header shows "N to check"
   *  (warning tone) instead of the normal fill-count chip and auto-opens. Empty/undefined (every
   *  other caller, e.g. the resident apply page) renders exactly as before — purely additive. */
  flaggedKeys?: Record<string, string>;
}

export function SchemaFormRenderer({
  schema,
  values,
  onChange,
  fieldErrors = {},
  initialOpenKeys,
  flaggedKeys = {},
}: SchemaFormRendererProps): JSX.Element {
  const flaggedSectionKeys = schema.sections
    .filter((s) => s.fields.some((f) => flaggedKeys[f.key] !== undefined))
    .map((s) => s.key);
  const defaultOpen =
    initialOpenKeys ??
    Array.from(
      new Set([schema.sections[0]?.key, ...flaggedSectionKeys].filter((k): k is string => Boolean(k))),
    );
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set(defaultOpen));

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setValue = (key: string, value: string) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="td-schemaform">
      {schema.sections.map((section) => {
        const isOpen = openKeys.has(section.key) || flaggedSectionKeys.includes(section.key);
        const visibleFields = section.fields.filter((f) => fieldIsVisible(f, values));
        const flagCount = visibleFields.filter((f) => flaggedKeys[f.key] !== undefined).length;
        const count = sectionCount(section, values);
        return (
          <div key={section.key} className={`td-formsection${isOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="td-formsection__head"
              aria-expanded={isOpen}
              onClick={() => toggle(section.key)}
            >
              <span className="td-formsection__headleft">
                <h2 className="td-formsection__title">{section.title}</h2>
                {flagCount > 0 ? (
                  <span className="td-formsection__count has-flags">
                    {flagCount} to check
                  </span>
                ) : count ? (
                  <span className={`td-formsection__count${count.hasValues ? " has-values" : ""}`}>
                    {count.label}
                  </span>
                ) : null}
              </span>
              <span className="td-formsection__chevron" aria-hidden="true">
                ➔
              </span>
            </button>
            {isOpen ? (
              <div className="td-formsection__body">
                {section.help ? <p className="td-formsection__help">{section.help}</p> : null}
                {visibleFields.map((field) => (
                  <SchemaFieldRenderer
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    onChange={(v) => setValue(field.key, v)}
                    error={fieldErrors[field.key]}
                    flagHelp={flaggedKeys[field.key]}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** A field with a `showWhen` is only rendered/validated when the referenced sibling equals the
 *  target value (ledger §A.3 "one-level showWhen conditional-show — no nested boolean trees"). The
 *  referenced field may itself be a checkbox-group (comma-joined selections, e.g. Building's
 *  Site Elements) — `equals` then matches against membership, not exact string equality. An UNSET
 *  boolean-checkbox reference (never toggled) reads as `"false"` — mirrors the checkbox control's
 *  own default (unchecked) so a `showWhen: {equals: "false"}` field (e.g. the reg# fields, shown
 *  when "Self" is NOT checked) is visible from first render, not only after the box is touched once. */
function fieldIsVisible(field: FormField, values: FormValues): boolean {
  if (!field.showWhen) return true;
  const raw = values[field.showWhen.field] ?? "false";
  return raw === field.showWhen.equals || raw.split(",").includes(field.showWhen.equals);
}

/** Section header count chip — "N selected" for a checkbox-group-led section (Site Elements convention,
 *  ui-spec §2), "N of M" fill-rate for a plain-field section, or null for a section with no fields
 *  worth counting (e.g. one holding only a section-note). */
function sectionCount(
  section: FormSchema["sections"][number],
  values: FormValues,
): { label: string; hasValues: boolean } | null {
  const countable = section.fields.filter((f) => f.type !== "section-note" && !f.showWhen);
  if (!countable.length) return null;
  const group = countable.find((f) => f.type === "checkbox-group");
  if (group) {
    const selected = (values[group.key] ?? "").split(",").filter(Boolean);
    return { label: `${selected.length} selected`, hasValues: selected.length > 0 };
  }
  const filled = countable.filter((f) => (values[f.key] ?? "").trim() !== "").length;
  if (filled === 0) return { label: "Not started", hasValues: false };
  return { label: `${filled} of ${countable.length}`, hasValues: true };
}

interface FieldRendererProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** OCR "double-check" confidence flag helper text (permit-poa-digest.md) — presence (even an
   *  empty string) means flagged; undefined means not flagged. */
  flagHelp?: string;
}

/** Icon + text "Double-check" badge (never color alone, WCAG 1.4.1) — the flagged-field precedent
 *  mirrors the existing .td-mtgimp-statuspill confidence-pill convention (console.css). */
function DoubleCheckBadge(): JSX.Element {
  return (
    <span className="td-flagbadge">
      <AlertIcon width={11} height={11} />
      Double-check
    </span>
  );
}

function SchemaFieldRenderer({ field, value, onChange, error, flagHelp }: FieldRendererProps): JSX.Element {
  const fieldId = `sf-${field.key}`;
  const flagged = flagHelp !== undefined;

  if (field.type === "section-note") {
    return (
      <p className="td-formsection__note" role="note">
        {field.help || field.label}
      </p>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="td-checkbox-row" htmlFor={fieldId}>
        <input
          id={fieldId}
          type="checkbox"
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
        />
        {field.label}
        {field.required ? (
          <span className="td-field__req" aria-hidden="true">
            {" "}
            *
          </span>
        ) : null}
        {flagged ? <DoubleCheckBadge /> : null}
      </label>
    );
  }

  if (field.type === "checkbox-group") {
    const selected = new Set(value.split(",").filter(Boolean));
    const toggleOpt = (v: string) => {
      const next = new Set(selected);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange(Array.from(next).join(","));
    };
    return (
      <fieldset className="td-fieldset" role="group" aria-labelledby={`${fieldId}-legend`}>
        <legend className="td-fieldset__legend" id={`${fieldId}-legend`}>
          {field.label}
          {field.required ? (
            <span className="td-field__req" aria-hidden="true">
              {" "}
              *
            </span>
          ) : null}
        </legend>
        <div className="td-elementgrid">
          {(field.options ?? []).map((opt) => (
            <div key={opt.value} className={`td-element-row${selected.has(opt.value) ? " is-checked" : ""}`}>
              <label className="td-element-row__toggle">
                <input
                  type="checkbox"
                  checked={selected.has(opt.value)}
                  onChange={() => toggleOpt(opt.value)}
                />
                <span className="td-element-row__label">{opt.label}</span>
              </label>
            </div>
          ))}
        </div>
        {error ? (
          <p className="td-field__error">{error}</p>
        ) : null}
      </fieldset>
    );
  }

  const baseHelper = field.help ? `${field.help}${field.unit ? ` (${field.unit})` : ""}` : field.unit;
  const helper = flagged ? flagHelp || baseHelper : baseHelper;
  const label = flagged ? (
    <>
      {field.label}
      <DoubleCheckBadge />
    </>
  ) : (
    field.label
  );

  return (
    <div className={flagged ? "td-field--flag" : undefined}>
      <Field id={fieldId} label={label} required={field.required} helper={helper} error={error}>
      {(aria) => {
        switch (field.type) {
          case "textarea":
            return (
              <textarea
                {...aria}
                className="td-input td-textarea"
                rows={3}
                maxLength={field.maxLen || undefined}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            );
          case "select":
            return (
              <select
                {...aria}
                className="td-select"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              >
                <option value="">Choose…</option>
                {(field.options ?? []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            );
          case "number":
            return field.unit ? (
              <div className="td-unit-input">
                <input
                  {...aria}
                  className="td-input"
                  type="number"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
                <span className="td-unit-input__suffix">{field.unit}</span>
              </div>
            ) : (
              <input
                {...aria}
                className="td-input"
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            );
          case "date":
            return (
              <input
                {...aria}
                className="td-input"
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            );
          case "dimensions":
            return <DimensionsInput aria={aria} value={value} unit={field.unit} onChange={onChange} />;
          default:
            return (
              <input
                {...aria}
                className="td-input"
                type="text"
                maxLength={field.maxLen || undefined}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            );
        }
      }}
      </Field>
    </div>
  );
}

/** dimensions field type — a small composite L x W(x H) input, stored on the wire as "10x12" (or
 *  "10x12x8" with a height) — one field type instead of three loose numbers (ledger §A.1). */
function DimensionsInput({
  aria,
  value,
  unit,
  onChange,
}: {
  aria: { id: string };
  value: string;
  unit?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const parts = value.split("x");
  const [l, w, h] = [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
  const emit = (nl: string, nw: string, nh: string) => {
    const clean = [nl, nw, nh].filter((p, i) => p !== "" || i < 2);
    onChange(clean.join("x"));
  };
  const inputId = useId();
  return (
    <div className="td-dims">
      <input
        id={aria.id}
        className="td-input"
        type="number"
        aria-label="Length"
        value={l}
        onChange={(e) => emit(e.target.value, w, h)}
      />
      <span aria-hidden="true">×</span>
      <input
        id={`${inputId}-w`}
        className="td-input"
        type="number"
        aria-label="Width"
        value={w}
        onChange={(e) => emit(l, e.target.value, h)}
      />
      {unit ? <span>{unit}</span> : null}
    </div>
  );
}
